<script setup lang="ts">
import type { Component } from 'vue';
import {
  CircleCheckBig,
  Info,
  Home,
  Building2,
  FileCheck,
  BookOpen,
  Megaphone,
  GraduationCap,
  Briefcase,
  Users,
  Quote,
} from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';

const BuildingIcon = (LucideIcons as Record<string, Component>).Building;
const MessageSquareIcon = (LucideIcons as Record<string, Component>).MessageSquare;
const FileTextIcon =
  (LucideIcons as Record<string, Component>).FileText ??
  (LucideIcons as Record<string, Component>).FileCheck;

/** Map Wagtail help icon keys to Lucide components (matches backend HELP_ICON_CHOICES). */
const HELP_ICON_MAP: Record<string, Component> = {
  'circle-check-big': CircleCheckBig,
  info: Info,
  home: Home,
  building: BuildingIcon ?? Building2,
  'building-2': Building2,
  'file-check': FileCheck,
  'file-text': FileTextIcon ?? FileCheck,
  'book-open': BookOpen,
  'message-square': MessageSquareIcon ?? Info,
  megaphone: Megaphone,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  users: Users,
  quote: Quote,
};

function iconComponent(iconName: string | null | undefined): Component {
  if (!iconName || typeof iconName !== 'string') return Info;
  return HELP_ICON_MAP[iconName] ?? Info;
}

defineProps<{
  heading?: string | null;
  icon?: string | null;
  text?: string | null;
}>();
</script>

<template>
  <section v-if="heading || text || icon" class="block-feature-card">
    <div class="block-feature-card__card">
      <div v-if="icon" class="block-feature-card__icon-wrap" aria-hidden="true">
        <component
          :is="iconComponent(icon)"
          class="block-feature-card__icon"
        />
      </div>
      <h3 v-if="heading" class="block-feature-card__title">
        {{ heading }}
      </h3>
      <p v-if="text" class="block-feature-card__text">
        {{ text }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.block-feature-card {
  margin: 1em 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.block-feature-card__card {
  flex: 1;
  min-height: 0;
  padding: 0.75rem 1.25rem;
  background: var(--color-card-bg, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.75rem;
}

@media (min-width: 640px) {
  .block-feature-card__card {
    padding: 1rem 1.5rem;
    border-radius: 1rem;
  }
}

.block-feature-card__icon-wrap {
  width: 2rem;
  height: 2rem;
  border-radius: 16px;
  background: var(--color-vision-mission-icon-bg, rgba(31, 122, 90, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

@media (min-width: 640px) {
  .block-feature-card__icon-wrap {
    width: 2.5rem;
    height: 2.5rem;
    margin-bottom: 0.75rem;
  }
}

.block-feature-card__icon {
  width: 1rem;
  height: 1rem;
  color: var(--color-link, #1f7a5a);
}

@media (min-width: 640px) {
  .block-feature-card__icon {
    width: 1.25rem;
    height: 1.25rem;
  }
}

.block-feature-card__title {
  margin: 0 0 0.375rem;
  font-size: 1rem;
  font-weight: 600;
  line-height: 28px;
  color: rgba(26, 26, 26, 1);
  width: 324px;
  height: 28px;
}

@media (min-width: 640px) {
  .block-feature-card__title {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }
}

@media (min-width: 768px) {
  .block-feature-card__title {
    font-size: 1.25rem;
  }
}

.block-feature-card__text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--color-text-muted, #666);
}

@media (min-width: 640px) {
  .block-feature-card__text {
    font-size: 0.9375rem;
  }
}

@media (min-width: 768px) {
  .block-feature-card__text {
    font-size: 1rem;
  }
}
</style>
